// Export WeSafe HTML documents to PDF with no extra dependency. We print
// IN-PLACE (inside the app document) rather than opening a blank popup, so:
//   - the browser's footer shows the real app URL instead of "about:blank";
//   - the app's stylesheets (TableStyle.css for SWMS blue tables, etc.) apply;
//   - a print-only stylesheet forces white paper + readable dark text while
//     keeping the styled table cells white on their dark/blue background.
// The browser still draws its own header/footer (date/title/page) per the print
// dialog's "Headers and footers" toggle — that is not controllable from code.

export type ExportSection = { title?: string | null; html: string };

const PRINT_ROOT_ID = 'wesafe-print-root';
const PRINT_STYLE_ID = 'wesafe-print-style';

const PRINT_CSS = `
@media print {
  /* Show only the print content. */
  body > *:not(#${PRINT_ROOT_ID}) { display: none !important; }
  #${PRINT_ROOT_ID} { display: block !important; }

  @page { size: A4; margin: 16mm; }

  #${PRINT_ROOT_ID} { background: #fff !important; }
  #${PRINT_ROOT_ID} .wesafe-doc {
    background: #fff !important;
    color: #1a1a1a !important;
    font-family: "Helvetica Neue", Arial, sans-serif;
    font-size: 11pt;
    line-height: 1.6;
  }
  #${PRINT_ROOT_ID} .wesafe-doc * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }

  /* Force readable dark prose, overriding dark-theme inline colours. */
  #${PRINT_ROOT_ID} .wesafe-doc p,
  #${PRINT_ROOT_ID} .wesafe-doc li,
  #${PRINT_ROOT_ID} .wesafe-doc div,
  #${PRINT_ROOT_ID} .wesafe-doc span,
  #${PRINT_ROOT_ID} .wesafe-doc strong,
  #${PRINT_ROOT_ID} .wesafe-doc h1,
  #${PRINT_ROOT_ID} .wesafe-doc h2,
  #${PRINT_ROOT_ID} .wesafe-doc h3,
  #${PRINT_ROOT_ID} .wesafe-doc h4 { color: #1a1a1a !important; }

  /* ...but the styled SWMS table cells keep white text on their dark/blue bg
     (more specific, so it wins). */
  #${PRINT_ROOT_ID} .wesafe-doc .style1 td,
  #${PRINT_ROOT_ID} .wesafe-doc .style1 td * { color: #fff !important; }

  #${PRINT_ROOT_ID} .doc-section { break-inside: auto; }
  #${PRINT_ROOT_ID} .doc-section + .doc-section { margin-top: 10pt; }
  #${PRINT_ROOT_ID} h1, #${PRINT_ROOT_ID} h2, #${PRINT_ROOT_ID} h3, #${PRINT_ROOT_ID} h4 { break-after: avoid; }
  #${PRINT_ROOT_ID} tr, #${PRINT_ROOT_ID} li, #${PRINT_ROOT_ID} img { break-inside: avoid; }
}
`;

function escapeHtml(s: string): string {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

export function exportDocumentToPdf(title: string, sections: ExportSection[]): void {
  // Clean up any leftovers from a prior run.
  document.getElementById(PRINT_ROOT_ID)?.remove();
  document.getElementById(PRINT_STYLE_ID)?.remove();

  const body = sections
    .map((s) => {
      const heading = s.title ? `<h2>${escapeHtml(s.title)}</h2>` : '';
      return `<section class="doc-section">${heading}${s.html || ''}</section>`;
    })
    .join('\n');

  const root = document.createElement('div');
  root.id = PRINT_ROOT_ID;
  root.style.display = 'none'; // hidden on screen; the print stylesheet reveals it
  root.innerHTML = `<div class="wesafe-doc safetyDoc">${body}</div>`;
  document.body.appendChild(root);

  const style = document.createElement('style');
  style.id = PRINT_STYLE_ID;
  style.textContent = PRINT_CSS;
  document.head.appendChild(style);

  const prevTitle = document.title;
  document.title = title; // becomes the default "Save as PDF" filename

  const cleanup = () => {
    document.getElementById(PRINT_ROOT_ID)?.remove();
    document.getElementById(PRINT_STYLE_ID)?.remove();
    document.title = prevTitle;
    window.removeEventListener('afterprint', cleanup);
  };
  window.addEventListener('afterprint', cleanup);

  // Let the DOM settle, then print. Fallback cleanup if afterprint never fires.
  setTimeout(() => {
    window.print();
    setTimeout(cleanup, 1000);
  }, 100);
}
